<template>
  <div class="svg-world-map">
    <svg :viewBox="World.viewBox" class="map-svg" preserveAspectRatio="xMidYMid meet">
      <!-- No background - use card's background -->

      <!-- Countries -->
      <g>
        <path
          v-for="location in World.locations"
          :key="location.id"
          :d="location.path"
          :fill="getCountryColor(location.id)"
          :stroke="getCountryStroke(location.id)"
          :stroke-width="0.5"
          @mouseover="handleCountryHover($event, location)"
          @mouseout="handleCountryLeave"
          @click="handleCountryClick(location)"
          class="country-path"
        />
      </g>
    </svg>

    <!-- Tooltip -->
    <div
      v-if="tooltip.show"
      class="map-tooltip"
      :style="{
        left: tooltip.x + 'px',
        top: tooltip.y + 'px'
      }"
    >
      <div v-if="tooltip.country">
        <div class="tooltip-title">
          <span :class="`flag-icon flag-icon-${getCountryFlag(hoveredCountry)}`" style="margin-right: 8px;"></span>
          {{ tooltip.country.country }}
        </div>
        <div class="tooltip-stats">
          <div class="stat-row">
            <span class="grey--text">{{ $t('broker.dashboard.map.traders') }}</span>
            <strong>{{ formatNumber(tooltip.country.traders) }}</strong>
          </div>
          <div class="stat-row">
            <span class="grey--text">{{ $t('broker.dashboard.map.volume') }}</span>
            <strong>{{ formatNumber(tooltip.country.volume) }} {{ $t('broker.dashboard.map.lots') }}</strong>
          </div>
          <div class="stat-row">
            <span class="grey--text">{{ $t('broker.dashboard.map.marketShare') }}</span>
            <strong>{{ tooltip.country.percentage }}%</strong>
          </div>
        </div>
      </div>
      <div v-else>
        <div class="tooltip-title">
          <span :class="`flag-icon flag-icon-${getCountryFlag(hoveredCountry)}`" style="margin-right: 8px;"></span>
          {{ tooltip.countryName }}
        </div>
        <div class="tooltip-no-data">{{ $t('broker.dashboard.map.noBrokerPresence') }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import World from '@svg-maps/world'

export default {
  name: 'SvgWorldMap',
  props: {
    countries: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      World: World,
      hoveredCountry: null,
      tooltip: {
        show: false,
        x: 0,
        y: 0,
        country: null,
        countryName: ''
      }
    }
  },
  computed: {
    countryDataMap() {
      const map = {}
      this.countries.forEach(country => {
        // Map country codes to SVG location IDs
        // SVG uses lowercase 2-letter ISO codes
        map[country.countryCode] = country
      })
      return map
    },
    maxTraders() {
      return Math.max(...this.countries.map(c => c.traders), 1)
    }
  },
  methods: {
    getCountryColor(countryId) {
      const country = this.countryDataMap[countryId]

      // If hovered, show hover color
      if (this.hoveredCountry === countryId) {
        return country ? '#28ea96' : '#d0d0d0'  // Primary green on hover for countries with presence
      }

      // If country has data, show light green
      if (country) {
        return '#a8f5d4'  // Light green for all countries with broker presence
      }

      // No data - light gray
      return '#e8e8e8'
    },
    getCountryStroke(countryId) {
      if (this.hoveredCountry === countryId) {
        return '#28ea96'
      }
      return '#ffffff'
    },
    handleCountryHover(event, location) {
      this.hoveredCountry = location.id
      const country = this.countryDataMap[location.id]
      const rect = event.target.getBoundingClientRect()

      this.tooltip = {
        show: true,
        x: rect.left + rect.width / 2,
        y: rect.top - 10,
        country: country,
        countryName: location.name
      }
    },
    handleCountryLeave() {
      this.hoveredCountry = null
      this.tooltip.show = false
    },
    handleCountryClick(location) {
      const country = this.countryDataMap[location.id]
      if (country) {
        this.$emit('country-click', country)
      }
    },
    formatNumber(num) {
      if (num >= 1000000) {
        return (num / 1000000).toFixed(1) + 'M'
      } else if (num >= 1000) {
        return (num / 1000).toFixed(1) + 'K'
      }
      return num.toLocaleString()
    },
    getCountryFlag(countryCode) {
      // Map country codes to ISO 3166-1 alpha-2 flag codes
      // The map data uses lowercase country codes
      const flagMap = {
        // Americas
        'us': 'us',
        'usa': 'us',
        'united states': 'us',
        'ca': 'ca',
        'canada': 'ca',
        'mx': 'mx',
        'mexico': 'mx',
        'br': 'br',
        'brazil': 'br',
        'ar': 'ar',
        'argentina': 'ar',
        'cl': 'cl',
        'chile': 'cl',
        'co': 'co',
        'colombia': 'co',
        'pe': 'pe',
        'peru': 'pe',
        've': 've',
        'venezuela': 've',
        'ec': 'ec',
        'ecuador': 'ec',
        'uy': 'uy',
        'uruguay': 'uy',
        'py': 'py',
        'paraguay': 'py',
        'bo': 'bo',
        'bolivia': 'bo',

        // Europe
        'gb': 'gb',
        'uk': 'gb',
        'united kingdom': 'gb',
        'de': 'de',
        'germany': 'de',
        'fr': 'fr',
        'france': 'fr',
        'es': 'es',
        'spain': 'es',
        'it': 'it',
        'italy': 'it',
        'nl': 'nl',
        'netherlands': 'nl',
        'be': 'be',
        'belgium': 'be',
        'ch': 'ch',
        'switzerland': 'ch',
        'at': 'at',
        'austria': 'at',
        'pt': 'pt',
        'portugal': 'pt',
        'pl': 'pl',
        'poland': 'pl',
        'se': 'se',
        'sweden': 'se',
        'no': 'no',
        'norway': 'no',
        'dk': 'dk',
        'denmark': 'dk',
        'fi': 'fi',
        'finland': 'fi',
        'ie': 'ie',
        'ireland': 'ie',
        'cz': 'cz',
        'czech republic': 'cz',
        'hu': 'hu',
        'hungary': 'hu',
        'ro': 'ro',
        'romania': 'ro',
        'gr': 'gr',
        'greece': 'gr',
        'ua': 'ua',
        'ukraine': 'ua',
        'ru': 'ru',
        'russia': 'ru',

        // Asia Pacific
        'cn': 'cn',
        'china': 'cn',
        'jp': 'jp',
        'japan': 'jp',
        'kr': 'kr',
        'south korea': 'kr',
        'in': 'in',
        'india': 'in',
        'au': 'au',
        'australia': 'au',
        'nz': 'nz',
        'new zealand': 'nz',
        'sg': 'sg',
        'singapore': 'sg',
        'my': 'my',
        'malaysia': 'my',
        'th': 'th',
        'thailand': 'th',
        'id': 'id',
        'indonesia': 'id',
        'ph': 'ph',
        'philippines': 'ph',
        'vn': 'vn',
        'vietnam': 'vn',

        // Middle East & Africa
        'za': 'za',
        'south africa': 'za',
        'eg': 'eg',
        'egypt': 'eg',
        'sa': 'sa',
        'saudi arabia': 'sa',
        'ae': 'ae',
        'united arab emirates': 'ae',
        'il': 'il',
        'israel': 'il',
        'tr': 'tr',
        'turkey': 'tr',
        'ng': 'ng',
        'nigeria': 'ng',
        'ke': 'ke',
        'kenya': 'ke',
        'ma': 'ma',
        'morocco': 'ma'
      }

      const code = countryCode?.toLowerCase()
      // Try direct mapping first, then try first two characters
      return flagMap[code] || flagMap[code?.substring(0, 2)] || code?.substring(0, 2) || 'xx'
    }
  },
  mounted() {
    console.log('World map loaded with', World.locations.length, 'countries')
    console.log('ViewBox:', World.viewBox)
    console.log('Sample country:', World.locations[0])
  }
}
</script>

<style lang="scss" scoped>
.svg-world-map {
  position: relative;
  width: 100%;
  padding-bottom: 65.94%; // Aspect ratio 666/1010 = 65.94%
  background: transparent;
  overflow: hidden;

  .map-svg {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: block;
  }

  .country-path {
    transition: fill 0.2s ease, stroke 0.2s ease;
    cursor: pointer;
    vector-effect: non-scaling-stroke;

    &:hover {
      stroke-width: 1.5;
      opacity: 0.9;
    }
  }

  .map-tooltip {
    position: fixed;
    background: white;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    padding: 0;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 1000;
    transform: translate(-50%, -100%);
    margin-top: -8px;
    pointer-events: none;
    min-width: 180px;

    .tooltip-title {
      font-weight: 600;
      font-size: 14px;
      padding: 10px 12px 8px;
      border-bottom: 1px solid #f0f0f0;
      background: #fafafa;
      border-radius: 6px 6px 0 0;
    }

    .tooltip-stats {
      padding: 10px 12px;

      .stat-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-size: 12px;
        line-height: 1.8;
      }
    }

    .tooltip-no-data {
      padding: 8px 12px;
      font-size: 12px;
      color: #999;
      font-style: italic;
    }
  }
}
</style>